import React from "react";
import { FileText, Loader2 } from "lucide-react";
import { TimecardRequestItem } from "../../../types/response/timecard";
import { convertDecimalHoursToHM } from "../../../utils/employeeTimeCardhelper";
import { DateTimeUtil } from "../../../utils";

const EARNING_CODE_LABELS: Record<string, string> = {
    REG: "Regular",
    OT: "Overtime",
    EXTRA_HOURS: "Extra Hours",
};

const PROCESSING_STATUS_LABELS: Record<string, string> = {
    "0": "Pending",
    "1": "Approved",
};

function getEarningCodeLabel(code: string): string {
    const upper = (code || "").toUpperCase().replace(/\s+/g, "_");
    return EARNING_CODE_LABELS[upper] ?? (code || "—");
}

function getProcessingStatusLabel(status: string): string {
    return PROCESSING_STATUS_LABELS[status] ?? status ?? "—";
}

function getRequestDuration(item: TimecardRequestItem): string {
    const calculatedDuration = DateTimeUtil.getDuration(item.start_datetime, item.end_datetime);

    if (calculatedDuration !== "0h 0m") {
        return calculatedDuration;
    }

    const parsedHours = Number(item.hours);
    return Number.isFinite(parsedHours) ? convertDecimalHoursToHM(parsedHours) : "0h 0m";
}

function getRequestTimeRange(item: TimecardRequestItem): string {
    const startTime = DateTimeUtil.getFormattedTime(item.start_datetime, "Missing punch");
    const endTime = DateTimeUtil.getFormattedTime(item.end_datetime, "Missing punch");

    return `${startTime} – ${endTime}`;
}

export interface RequestsAndAdjustmentsCardProps {
    requests: TimecardRequestItem[];
    loading?: boolean;
}

const RequestsAndAdjustmentsCard: React.FC<RequestsAndAdjustmentsCardProps> = ({
    requests,
    loading = false,
}) => {
    if (loading) {
        return (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                        <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-800">Requests</h3>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500">Loading requests…</span>
                </div>
            </div>
        );
    }

    const count = requests?.length ?? 0;
    if (count === 0) {
        return null;
    }

    return (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                        <FileText className="h-4 w-4 text-amber-600" aria-hidden />
                    </div>
                    <h3 className="text-base font-semibold text-gray-800">
                        Requests
                    </h3>
                </div>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                    {count} {count === 1 ? "request" : "requests"}
                </span>
            </div>
            <ul className="space-y-2">
                {requests.map((item) => {
                    const label = getEarningCodeLabel(item.earning_code);
                    const duration = getRequestDuration(item);
                    const timeRange = getRequestTimeRange(item);
                    const status = getProcessingStatusLabel(item.processing_status);
                    const isApproved = item.processing_status === "1";
                    const date = DateTimeUtil.getFormattedDate(item.start_datetime);
                    return (
                        <li
                            key={item.os_employee_timesheet_requests_id}
                            className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3 transition-colors hover:bg-gray-50"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-base font-medium text-gray-900">
                                        {timeRange}
                                        <span className="ml-2 text-gray-500">({duration})</span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                                        <span className="font-medium tabular-nums text-gray-600">{date}</span>
                                        <span aria-hidden>•</span>
                                        <span>{label}</span>
                                        {item.request_notes ? (
                                            <>
                                                <span aria-hidden>•</span>
                                                <span className="truncate">{item.request_notes}</span>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                                <span
                                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                                        isApproved
                                            ? "bg-emerald-100 text-emerald-700"
                                            : "bg-amber-100 text-amber-700"
                                    }`}
                                >
                                    {status}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default RequestsAndAdjustmentsCard;
